import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';

import { DEFAULT_HEADERS, DEFAULT_REQUEST_TIMEOUT, WITH_CREDENTIALS } from './config';
import setupInterceptors from './interceptors';

// Initialize interceptors
setupInterceptors();

export type RequestMethod = 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';

// Common parameter interface for all requests
export interface CommonRequestParams {
  url: string;
  params?: Record<string, unknown>;
  headers?: Record<string, string>;
  withCredentials?: boolean;
  timeout?: number;
}

// Interface for request functions with data (POST, PUT, PATCH)
export interface DataRequestParams<TRequest> extends CommonRequestParams {
  data?: TRequest;
}

// Request options for the internal request function
export interface RequestOptions<T> extends CommonRequestParams {
  method: RequestMethod;
  data?: T;
}

/**
 * Generic request function to handle API calls
 * @param options Request options
 * @returns Promise with the response data
 */
const request = async <TResponse = unknown, TRequest = unknown>({
  method,
  url,
  params,
  data,
  headers = {},
  withCredentials = WITH_CREDENTIALS,
  timeout = DEFAULT_REQUEST_TIMEOUT,
}: RequestOptions<TRequest>): Promise<TResponse> => {
  try {
    const config: AxiosRequestConfig = {
      method,
      url,
      params,
      data,
      headers: { ...DEFAULT_HEADERS, ...headers },
      withCredentials,
      timeout,
    };

    const response: AxiosResponse<TResponse> = await axios(config);
    return response.data;
  } catch (error) {
    // Handle and transform errors
    if (axios.isAxiosError(error)) {
      const message = error.response?.data?.message || error.message;

      throw new Error(message);
    }
    throw error;
  }
};

export const get = <TResponse = unknown>(props: CommonRequestParams): Promise<TResponse> => {
  return request<TResponse>({
    method: 'GET',
    ...props,
  });
};

export const post = <TResponse = unknown, TRequest = unknown>(
  props: DataRequestParams<TRequest>
): Promise<TResponse> => {
  return request<TResponse, TRequest>({
    method: 'POST',
    ...props,
  });
};

export const put = <TResponse = unknown, TRequest = unknown>(
  props: DataRequestParams<TRequest>
): Promise<TResponse> => {
  return request<TResponse, TRequest>({
    method: 'PUT',
    ...props,
  });
};

export const del = <TResponse = unknown>(props: CommonRequestParams): Promise<TResponse> => {
  return request<TResponse>({
    method: 'DELETE',
    ...props,
  });
};

export const patch = <TResponse = unknown, TRequest = unknown>(
  props: DataRequestParams<TRequest>
): Promise<TResponse> => {
  return request<TResponse, TRequest>({
    method: 'PATCH',
    ...props,
  });
};
